import React from 'react';
import { NON_MEMBER_GUEST_MEMBERSHIP_TYPE } from '../constants/membership';
import { isNonMemberGuestMembershipType } from '../utils/membership';
import DataRow, { DataRowHeader } from './ui/DataRow';
import Field from './ui/Field';
import Section from './ui/Section';
import Toggle from './ui/Toggle';
import AddGuestForm from './AddGuestForm';

/*
 * The club's members.
 *
 * Ten columns of bordered table with a dark green header bar. Role was in
 * there twice — a coloured tag in a "Role" column and, three columns later,
 * a select in "Actions" showing the same value. Financial status was a
 * coloured circle you had to know was clickable, and every name was
 * #2196F3 underlined, a blue belonging to no club palette.
 *
 * Now one row per member on DataRow, one control per thing, and the widths
 * are declared once for both the header and the rows. Ten columns still do
 * not fit a phone, and pretending otherwise by wrapping each record into a
 * twelve-line block would not help, so this list keeps its columns and
 * scrolls sideways inside its own box.
 */

const CELL_MUTED = { color: 'var(--gs-text-muted)' };
const CELL_FAINT = { color: 'var(--gs-text-faint)', fontStyle: 'italic' };

/** The compact select used in a row: shorter than a form field, because
 *  forty of them stacked at 44px turns the page into a mile of chrome. */
const rowSelectStyle = { minHeight: 'var(--gs-control-sm)', padding: '0 8px', fontSize: 'var(--gs-text-sm)', width: '100%' };

// The filter row is a form, so its selects are form height, as on New members.
const compactSelect = { minHeight: 'var(--gs-control-md)', padding: '0 8px' };
const distinct = (rows, key) => [...new Set(rows.map(r => r[key]).filter(Boolean))].sort();

function AdminMembersTab({ API, toggleVerified, availableRoles, changeRole, clubs, confirmMembership, currentUser, filteredUsers, getClubOptionsForUser, isSuperAdmin, loading, openProfile, pendingMembers, roleColor, setMergeSource, setMergeTarget, setReviewMember, setUsers, setUsersClubFilter, showToast, updateUserClassification, users, usersClubFilter }) {

  const [addingGuest, setAddingGuest] = React.useState(false);

  /* The same filters as New members, so the two lists are searched the same
     way. "Joining or renewing" means nothing once somebody is a member, so
     financial status takes its place — the other thing a club sorts its
     members by. */
  const [search, setSearch] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState('');
  const [playerFilter, setPlayerFilter] = React.useState('');
  const [financialFilter, setFinancialFilter] = React.useState('');

  const types = distinct(filteredUsers, 'membershipType');
  const playerTypes = distinct(filteredUsers, 'playerType');
  const q = search.trim().toLowerCase();
  const shownUsers = filteredUsers.filter(u => (!typeFilter || u.membershipType === typeFilter)
    && (!playerFilter || u.playerType === playerFilter)
    && (!financialFilter || (financialFilter === 'yes') === (u.isFinancial !== false))
    && (!q || [u.name, u.nickname, u.email].some(v => String(v || '').toLowerCase().includes(q))));

  const guestAdded = (created) => {
    setUsers(prev => [...prev, created]);
    setAddingGuest(false);
    showToast && showToast(`${created.name} added as a guest player`);
  };

  const setFinancial = u => {
    const newVal = u.isFinancial === false;
    fetch(`${API}/api/users/${u.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isFinancial: newVal }),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => {
        setUsers(prev => prev.map(x => x.id === u.id ? { ...x, isFinancial: newVal } : x));
        showToast(newVal ? `${u.name} marked financial` : `${u.name} marked non-financial`);
      })
      .catch(() => showToast('Failed to update', 'red'));
  };

  const pendingColumns = [
    { key: 'name', label: 'Name', grow: true, primary: true },
    {
      key: 'email', label: 'Email', width: 220,
      render: u => u.email
        ? <span style={CELL_MUTED}>{u.email}</span>
        : <span style={CELL_FAINT}>No email</span>,
    },
    {
      key: 'request', label: 'Request', width: 120,
      render: u => (
        <span className="w3-tag w3-round w3-amber" style={{ fontSize: 'var(--gs-text-xs)' }}>
          {u.membershipPendingIntent === 'renew' ? 'Renewal' : 'Membership'}
        </span>
      ),
    },
    {
      key: 'actions', label: '', width: 230, align: 'right',
      render: u => (
        <span style={{ display: 'flex', gap: 'var(--gs-space-2)', justifyContent: 'flex-end' }}>
          <button className="w3-button w3-round w3-theme-l4" style={{ minHeight: 'var(--gs-control-sm)' }}
            onClick={() => setReviewMember(u)}>
            Review
          </button>
          {currentUser.role === 'club-admin' ? (
            <button className="w3-button w3-round"
              style={{ minHeight: 'var(--gs-control-sm)', background: 'var(--gs-accent-fill, var(--club-30))', color: '#fff', fontWeight: 600 }}
              onClick={() => confirmMembership(u)}>
              Approve
            </button>
          ) : (
            <span style={{ ...CELL_FAINT, alignSelf: 'center' }}>Club admin approval required</span>
          )}
        </span>
      ),
    },
  ];

  const columns = [
    {
      /* Sized to hold a name, not to absorb the window. It stays the primary column,
         which is what makes it the reading-size column and gives it its own
         line on a phone — that is separate from taking the slack. */
      /* Capped, like every flexible column here. On a 3432px monitor there
         is more width than nine columns can use, and the question is only
         where the leftover goes. Letting a column absorb it puts two
         thousand pixels of blank between a name and its club, so the eye has
         to travel to read one row. Capping every column instead leaves the
         whitespace after the last one, where it reads as the end of the
         table rather than as a gap in it. */
      key: 'name', label: 'Name', grow: true, primary: true, minWidth: 170, maxWidth: 300,
      render: u => (
        <>
          <span style={{ color: 'var(--gs-accent-text, var(--club-30))', fontWeight: 500 }}>{u.name}</span>
          {u.nickname ? <span style={{ ...CELL_MUTED, marginLeft: 6 }}>({u.nickname})</span> : null}
        </>
      ),
    },
    /* No email column. It is not something an admin acts on from this list —
       it is not edited here, not sorted by, and not needed to recognise
       anybody — and it cost a third of the table to show. It is still on the
       member's own profile, and still on the pending-approval list above,
       where reviewing an application does depend on it. */
    isSuperAdmin && {
      key: 'club', label: 'Club', width: 140,
      render: u => u.Club
        ? <span style={{ ...CELL_MUTED, fontSize: 'var(--gs-text-sm)' }}>{u.Club.name}</span>
        : <span style={CELL_FAINT}>None</span>,
    },
    {
      key: 'membershipType', label: 'Membership', grow: true, minWidth: 130, maxWidth: 220,
      render: u => (isNonMemberGuestMembershipType(u.membershipType) ? (
        // System-managed: there is nothing to choose, so do not offer a menu.
        <span className="w3-tag w3-round w3-theme-l4" style={{ fontSize: 'var(--gs-text-xs)' }}>
          {NON_MEMBER_GUEST_MEMBERSHIP_TYPE}
        </span>
      ) : (
        <select className="w3-select" style={rowSelectStyle}
          aria-label={`Membership type — ${u.name}`}
          value={u.membershipType || ''}
          onChange={e => updateUserClassification(u.id, 'membershipType', e.target.value)}>
          <option value="">—</option>
          {getClubOptionsForUser(u, 'membershipType').map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      )),
    },
    {
      key: 'playerType', label: 'Player type', grow: true, minWidth: 120, maxWidth: 200,
      render: u => (
        <select className="w3-select" style={rowSelectStyle}
          aria-label={`Player type — ${u.name}`}
          value={u.playerType || ''}
          onChange={e => updateUserClassification(u.id, 'playerType', e.target.value)}>
          <option value="">—</option>
          {getClubOptionsForUser(u, 'playerType').map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      ),
    },
    {
      // Was two columns: a tag showing the role and a select setting it.
      key: 'role', label: 'Role', grow: true, minWidth: 120, maxWidth: 200,
      render: u => (u.id === currentUser.id ? (
        // You cannot change your own role, so it reads rather than edits.
        <span className={`w3-tag w3-round ${roleColor(u.role)}`} style={{ fontSize: 'var(--gs-text-xs)' }}>{u.role}</span>
      ) : (
        <select className="w3-select" style={rowSelectStyle}
          aria-label={`Role — ${u.name}`}
          value={u.role}
          onChange={e => changeRole(u.id, e.target.value)}>
          {availableRoles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      )),
    },
    {
      key: 'financial', label: 'Financial', width: 84, align: 'center',
      render: u => (
        <Toggle
          ariaLabel={`Financial — ${u.name}`}
          checked={u.isFinancial !== false}
          onChange={() => setFinancial(u)} />
      ),
    },
    {
      key: 'status', label: 'Status', width: 120,
      render: u => (u.id !== currentUser.id ? (
        <select className="w3-select" style={rowSelectStyle}
          aria-label={`Account status — ${u.name}`}
          value={u.accountStatus || 'unverified'}
          onChange={e => toggleVerified(u.id, e.target.value)}>
          <option value="unverified">Unverified</option>
          <option value="verified">Verified</option>
          <option value="blocked">Blocked</option>
        </select>
      ) : (
        <span className={`w3-tag w3-round ${u.accountStatus === 'verified' ? 'w3-green' : u.accountStatus === 'blocked' ? 'w3-red' : 'w3-amber'}`}
          style={{ fontSize: 'var(--gs-text-xs)' }}>
          {u.accountStatus || 'unverified'}
        </span>
      )),
    },
    {
      key: 'lastLogin', label: 'Last login', width: 90,
      render: u => (u.lastLoginAt
        ? <span style={{ ...CELL_MUTED, fontSize: 'var(--gs-text-sm)' }} title={new Date(u.lastLoginAt).toLocaleString()}>
            {new Date(u.lastLoginAt).toLocaleDateString()}
          </span>
        : <span style={CELL_FAINT}>Never</span>),
    },
    {
      // Merging is rare and destructive. It gets an icon at the end of the
      // row, not an amber block competing with everything else. Labelled,
      // because a bare icon at the end of the row was easy to miss and gave
      // no hint of what it did until it was hovered.
      key: 'merge', label: 'Merge', width: 64, align: 'right',
      render: u => (u.id !== currentUser.id ? (
        <button className="w3-button w3-round w3-hover-amber"
          style={{ padding: '4px 8px', color: 'var(--gs-text-faint)' }}
          onClick={() => { setMergeSource(u); setMergeTarget(''); }}
          title={`Merge ${u.name} into another user`} aria-label={`Merge ${u.name} into another user`}>
          <i className="fa fa-compress"></i>
        </button>
      ) : null),
    },
  ].filter(Boolean);

  // The admin panel's own background is the dark club colour, so these sit on
  // a white surface — without one the row text is unreadable against it.
  return (
    <div className="gs-panel">
      {pendingMembers.length > 0 && (
        <Section label={`Pending membership approvals (${pendingMembers.length})`}>
          <DataRowHeader columns={pendingColumns} />
          {pendingMembers.map(u => (
            <DataRow key={u.id} columns={pendingColumns} item={u} onClick={() => openProfile(u.id)} />
          ))}
        </Section>
      )}

      <Section
        label={shownUsers.length === filteredUsers.length
          ? `All users (${filteredUsers.length})`
          : `All users (${shownUsers.length} of ${filteredUsers.length})`}
        action={(
          <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--gs-space-3)', flexWrap: 'wrap' }}>
            <button type="button" className="w3-button w3-round poster-btn"
              style={{ minHeight: 'var(--gs-control-sm)' }}
              aria-expanded={addingGuest}
              onClick={() => setAddingGuest(v => !v)}>
              <i className={`fa ${addingGuest ? 'fa-times' : 'fa-plus'} w3-margin-right`} aria-hidden="true"></i>
              {addingGuest ? 'Cancel' : 'Add a guest player'}
            </button>
            {isSuperAdmin && (
              <select
                className="w3-select" style={{ minHeight: 'var(--gs-control-sm)', padding: '0 8px', minWidth: '180px' }}
                aria-label="Filter members by club"
                value={usersClubFilter}
                onChange={e => setUsersClubFilter(e.target.value)}>
                <option value="all">All clubs</option>
                {clubs.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
              </select>
            )}
          </span>
        )}>
        {/* A visitor written down without entering anybody in anything.
            Before this the only way to create one was the competition entry
            form, which enters the person filling it in — so an admin adding
            three visitors registered themselves three times and unpicked it
            afterwards. */}
        {addingGuest && <AddGuestForm users={users} onAdded={guestAdded} />}
        {/* Only the filters this list can use: a menu of one membership type
            filters nothing. */}
        <div className="gs-field-grid" style={{ '--gs-cols': 'repeat(auto-fit, minmax(170px, 1fr))', marginBottom: 'var(--gs-space-3)' }}>
          <Field label="Search" htmlFor="members-search">
            <input id="members-search" className="w3-input" type="search"
              placeholder="Name or email" value={search} onChange={e => setSearch(e.target.value)} />
          </Field>
          {types.length > 1 && (
            <Field label="Membership type" htmlFor="members-type">
              <select id="members-type" className="w3-select" style={compactSelect}
                value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                <option value="">All types</option>
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          )}
          {playerTypes.length > 1 && (
            <Field label="Player type" htmlFor="members-player">
              <select id="members-player" className="w3-select" style={compactSelect}
                value={playerFilter} onChange={e => setPlayerFilter(e.target.value)}>
                <option value="">All player types</option>
                {playerTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          )}
          <Field label="Financial" htmlFor="members-financial">
            <select id="members-financial" className="w3-select" style={compactSelect}
              value={financialFilter} onChange={e => setFinancialFilter(e.target.value)}>
              <option value="">Both</option>
              <option value="yes">Financial</option>
              <option value="no">Not financial</option>
            </select>
          </Field>
        </div>
        {loading ? (
          <div className="w3-center w3-padding"><i className="fa fa-spinner fa-spin fa-2x"></i></div>
        ) : (
          <div className="gs-datarow-scroll" style={{ '--gs-row-min': isSuperAdmin ? '1308px' : '1168px' }}>
            <DataRowHeader columns={columns} />
            {shownUsers.map(u => (
              <DataRow key={u.id} columns={columns} item={u} onClick={() => openProfile(u.id)} />
            ))}
            {shownUsers.length === 0 && filteredUsers.length > 0 && (
              <p style={{ color: 'var(--gs-text-faint)', padding: 'var(--gs-space-3) 0' }}>Nobody matches those filters.</p>
            )}
          </div>
        )}
      </Section>
    </div>
  );
}

export default AdminMembersTab;
